import { CartLinesUpdateEvent, CartErrorEvent } from 'https://cdn.shopify.com/storefront/standard-events.js';

class LpLookBundle extends HTMLElement {
  connectedCallback() {
    if (this.button) return; // Reconnecting the same element must not bind twice.
    this.cards = [...this.querySelectorAll('[data-product]')];
    this.button = this.querySelector('[data-add]');
    this.addEventListener('change', () => this.update());
    this.button.addEventListener('click', () => this.add());
    this.update();
    this.button.disabled = this.hasAttribute('data-off');
  }

  picked(card) {
    const options = [], props = {};
    card.querySelectorAll('select[data-option], input[data-option]:checked').forEach((c) => {
      options[c.dataset.option] = c.value;
      props[c.dataset.name] = c.value;
    });
    return { options, props };
  }

  // The JSON holds in-stock variants only, so anything that resolves is addable.
  variants(card) {
    return JSON.parse(card.querySelector('script').textContent);
  }

  variant(card) {
    const { options } = this.picked(card);
    return this.variants(card).find((v) => options.every((o, i) => o === v.options[i]));
  }

  // Sold-out combinations are never offered: rebuild each select from the in-stock variants
  // matching the picks before it (Metal first). Removed, not hidden — Safari ignores hidden <option>s.
  prune(card) {
    card.querySelectorAll('select[data-option]').forEach((select) => {
      const { options } = this.picked(card); // re-read: an earlier select may just have reset
      const i = Number(select.dataset.option);
      const values = [...new Set(this.variants(card).filter((v) => options.every((o, j) => j >= i || !o || o === v.options[j])).map((v) => v.options[i]))];
      const current = select.value;
      select.length = 1; // keep the placeholder
      values.forEach((value) => select.add(new Option(value, value)));
      select.value = values.includes(current) ? current : '';
    });
  }

  update() {
    let sum = 0;
    this.ready = !this.hasAttribute('data-off');
    for (const card of this.cards) {
      this.prune(card);
      const v = this.variant(card);
      if (v) {
        card.querySelector('[data-price]').textContent = formatter.format(v.price / 100);
        sum += v.price;
      } else this.ready = false;
    }
    // Preview only — FoxSell's Cart Transform is the pricing authority.
    // Until every option is picked a percentage look shows the server's "from" price (cheapest in-stock variants).
    const { strategy, fixed, percent, from } = this.dataset;
    const isFixed = strategy === 'fixed_pricing';
    const total = isFixed ? Number(fixed) : this.ready ? sum * (1 - Number(percent) / 100) : Number(from);
    this.querySelector('[data-total]').textContent = this.hasAttribute('data-off')
      ? 'bundle unavailable'
      : `${this.ready || isFixed ? '' : 'from '}${formatter.format(total / 100)}`;
    const sub = this.querySelector('[data-subtotal]');
    if (sub) sub.textContent = this.ready ? formatter.format(sum / 100) : '';
    if (this.ready) this.querySelector('[data-msg]').textContent = ''; // a fixed selection clears the last prompt
  }

  async add() {
    if (this.button.disabled) return;
    this.classList.add('lp-look__products--tried');
    const invalid = this.querySelector('select[data-option]:invalid');
    if (invalid) return invalid.reportValidity();
    const msg = this.querySelector('[data-msg]');
    // Every select is filled but a card has no in-stock variant for the combination (Metal not option 1).
    if (!this.ready) return (msg.textContent = "that combination isn't available — try another option");
    const { bundle, variant, title } = this.dataset;
    // Key names and item shape mirror assets/product-bundler.js:582-591 — FoxSell's Cart Transform matches on them.
    const items = this.cards.map((card) => ({
      variantId: String(this.variant(card).id),
      quantity: 1,
      category: card.dataset.category,
      type: 'product',
      title: card.dataset.title,
      properties: { ...this.picked(card).props, 'Part of': title, _Bundle: bundle },
    }));
    const details = this.cards.map((card) => ({ title: card.dataset.title, variantTitle: Object.values(this.picked(card).props).join(' / ') }));
    const line = {
      id: variant,
      quantity: 1,
      properties: {
        '__foxsell:dynamic_add_on_bundle_id': `${bundle}_${Date.now()}`,
        '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(items),
        '__bundle_item_details': JSON.stringify(details),
        _custom_product: 'True',
      },
    };
    const deferred = CartLinesUpdateEvent.createPromise();
    // Set the lock before dispatch: event listeners can synchronously call back into the element.
    this.button.disabled = true;
    // The event may have no subscribers; keep its rejected promise handled locally.
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'product',
      lines: [{ merchandiseId: variant, quantity: 1 }],
      promise: deferred.promise,
    }));
    msg.textContent = '';
    this.querySelector('[data-cart-link]').hidden = true;
    let added = false, declined = '', cart, json;
    try {
      // /cart/add.js (not Shopify.actions.updateCart) so the Rebuy Smart Cart's AJAX interception sees the add.
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [line], sections: ['cart-icon-bubble'], sections_url: window.location.pathname }) });
      added = res.ok; // A successful POST must not be retried if its response cannot be parsed.
      json = await res.json();
      // 422 = the cart declined the line (stock sold between render and click, or the Function said no).
      // Per Shopify's dispatch docs that RESOLVES with userErrors; only a failed request rejects.
      if (res.status === 422) declined = json.description || json.message || 'This bundle could not be added. Please check your selections.';
      else if (!res.ok) throw new Error(json.description || json.message || `cart/add.js responded ${res.status}`);
      const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) throw new Error('Unable to refresh bag');
      cart = await cartRes.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), userErrors: declined ? [{ message: declined, code: 'INVALID', field: ['lines', '0'] }] : [] });
    } catch (error) {
      // The real error belongs in the console; shoppers get plain copy.
      console.error(error);
      msg.textContent = added ? 'added to bag — view your bag to continue' : 'something went wrong — please try again';
      this.querySelector('[data-cart-link]').hidden = !added;
      // Reject the unavailable cart result without misreporting a confirmed add as failed.
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'SERVICE_UNAVAILABLE' }));
      deferred.reject(error);
      this.button.disabled = added;
      return;
    }
    if (declined) {
      msg.textContent = declined;
      this.button.disabled = false;
      return;
    }
    this.button.disabled = false;
    const smartCart = window.Rebuy?.SmartCart;
    if (!smartCart) return window.location.assign(window.routes.cart_url);
    // show() opens the drawer; it does not fetch the new cart.
    smartCart.setCart(cart);
    smartCart.show();
    // Rebuy owns the drawer but not the header bag count (same section swap as cart-notification.js).
    document.getElementById('cart-icon-bubble').innerHTML = new DOMParser().parseFromString(json.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section').innerHTML;
  }
}

if (!customElements.get('lp-look-bundle')) customElements.define('lp-look-bundle', LpLookBundle);
